import React, { useState, useMemo, useEffect } from "react";
import { Box, Button, Flex, FormControl, FormDialog, MenuItem, Select, TextField, Typography } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import ModelTraining, { LoraTrainingData } from "../../utils/model-training";
import LoraTrainingForm from "../lora-training-dialog/form";
import BulkUploadForm from "../bulk-generation-dialog/form";
import { validateModelName } from "./validate-model";
import { useToast } from "@adora/shared/hooks/use-toast";
import useExperimentProducts from "../../hooks/use-experiment-products";
import { useExperimentInfo } from "../../hooks/use-experiment-info";

interface LaunchExperimentDialogProps extends BaseDialogProps {
  brandId: number;
  experimentId?: number;
}

const DEFAULT_EXPERIMENT_TRAINING_PROMPT =
  "This task is to caption images. Use {TRIGGER_WORD} as a placeholder for the shoe (don't describe the shoe or use the word shoe in your description - just use {TRIGGER_WORD} as an all-encompassing term for it). Don't describe the {TRIGGER_WORD} and be declarative, don't use \"it appears to be\". If there is a person in the image, specify the race and gender if relevant. Don't preface with \"the image shows\" and be concise. Just describe the image directly, don't be interpretive. Example caption: 'A close-up of a {TRIGGER_WORD} against a plain background. The background is a simple, smooth surface, emphasizing the {TRIGGER_WORD}'.";

const defaultLoraTrainingParams: LoraTrainingData = {
  steps: 6000,
  loraRank: 128,
  batchSize: 3,
  learningRate: 0.0001,
  optimizer: "adamw8bit",
  resolution: "512,768,1024",
  captionDropoutRate: 0.05,
  themeId: -1,
  contentOrStyle: "balanced",
  wandbSampleInterval: 0,
  wandbSamplePrompt: "",
  baseModelNameOrPath: "",
  trainerName: "default"
};

const experimentInfoToLoraTrainingData = (
  experimentInfo: Record<string, unknown> | null
): Partial<LoraTrainingData> | undefined => {
  if (!experimentInfo) return undefined;

  return {
    steps: experimentInfo.steps as number,
    loraRank: experimentInfo.lora_rank as number,
    batchSize: experimentInfo.batch_size as number,
    learningRate: experimentInfo.learning_rate as number,
    optimizer: experimentInfo.optimizer as string,
    resolution: experimentInfo.resolution as string,
    captionDropoutRate: experimentInfo.caption_dropout_rate as number,
    contentOrStyle: experimentInfo.content_or_style as string,
    baseModelNameOrPath: experimentInfo.model as string,
    trainerName: (experimentInfo.replicate_trainer_name as string) || "default"
  };
};

const LaunchExperimentDialog: React.FC<LaunchExperimentDialogProps> = ({ brandId, experimentId, open, onClose }) => {
  const { info, isLoading } = useExperimentInfo(brandId, experimentId);
  const [batchName, setBatchName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [prompt, setPrompt] = useState<string>(DEFAULT_EXPERIMENT_TRAINING_PROMPT);
  const [triggerWord, setTriggerWord] = useState<string>("lor4modx");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [triggerLoraFinetuning, setTriggerLoraFinetuning] = useState<boolean>(true);
  const [triggerBulkUpload, setTriggerBulkUpload] = useState<boolean>(false);
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [referenceImages, setReferenceImages] = useState<File[] | null>(null);
  const [evalLossDataset, setEvalLossDataset] = useState<File | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>(undefined);
  const { products, isLoading: productsLoading, createProduct } = useExperimentProducts(brandId);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [showCreateNewProduct, setShowCreateNewProduct] = useState<boolean>(false);
  const [newProductName, setNewProductName] = useState<string>("");
  const [batchNameError, setBatchNameError] = useState<string | undefined>();
  const [autoEvalDatapath, setAutoEvalDatapath] = useState<string>("");
  const { openToast } = useToast();

  const initialLoraData = useMemo((): LoraTrainingData => {
    const loadedData = info?.training_batch_info ? experimentInfoToLoraTrainingData(info.training_batch_info) : {};
    return {
      ...defaultLoraTrainingParams,
      ...loadedData
    };
  }, [info]);

  const [loratrainingData, setLoraTrainingData] = useState<LoraTrainingData>(defaultLoraTrainingParams);

  useEffect(() => {
    setLoraTrainingData(initialLoraData);
  }, [initialLoraData]);

  const handleReferenceImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files || [])];
    if (files) {
      setReferenceImages(files);
    }
  };

  const handleEvalLossDatasetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setEvalLossDataset(file);
    }
  };

  const handleCaption = async (event: React.FormEvent<HTMLFormElement>) => {
    if (batchNameError) {
      return;
    }
    if (
      triggerLoraFinetuning &&
      (loratrainingData.themeId === -1 || loratrainingData.themeId === undefined || !referenceImages)
    ) {
      openToast("Theme and reference image are required for LoRA training");
      return;
    }

    setSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const files = formData.getAll("file") as File[];
    try {
      const { uploadCsv, uploadImages, uploadReferenceImage, uploadEvalLossDataset, launchExperiment } =
        ModelTraining();

      if (showCreateNewProduct && newProductName.trim() && !selectedProductId) {
        await handleCreateProduct();
      }

      let datasetPath = "";
      if (files.length > 0) {
        datasetPath = await uploadImages({
          brandId,
          files
        });
      }

      let referenceImagePaths: string[] = [];
      if (referenceImages) {
        referenceImagePaths = await Promise.all(
          referenceImages.map(async (image) => {
            return await uploadReferenceImage(image);
          })
        );
      }

      let bulkDatasetPath = "";
      if (triggerBulkUpload && bulkUploadFile) {
        bulkDatasetPath = await uploadCsv({
          brandId,
          file: bulkUploadFile
        });
      }

      let evalLossDatasetPath = "";
      if (evalLossDataset) {
        evalLossDatasetPath = await uploadEvalLossDataset(evalLossDataset);
      }

      const effectiveThemeId = loratrainingData.themeId !== -1 ? loratrainingData.themeId : undefined;

      let loraParams;
      if (triggerLoraFinetuning) {
        loraParams = { ...loratrainingData };
        if (evalLossDatasetPath) {
          loraParams.evalLossDatasetPath = evalLossDatasetPath;
        }
      }

      const response = await launchExperiment({
        brandId,
        name: batchName,
        description,
        referenceImagePaths,
        ...(datasetPath ? { datasetPath } : {}),
        captioningPrompt: prompt,
        triggerWord,
        autoEvalDatapath,
        ...(triggerLoraFinetuning && loraParams ? { trainLoraParams: loraParams } : {}),
        ...(effectiveThemeId ? { themeId: effectiveThemeId } : {}),
        ...(triggerBulkUpload && bulkDatasetPath ? { bulkDatasetPath } : {}),
        ...(selectedProductId ? { productId: selectedProductId } : {})
      });

      response.match(
        (error) => {
          if (error.tag === "validationError") {
            error.errors.map((message) => openToast(message));
          } else {
            openToast("An unknown error occurred");
          }
          setSubmitting(false);
        },
        () => {
          openToast("Experiment created successfully");
          setSubmitting(false);
          onClose(true);
        }
      );
    } catch (error) {
      console.error("Error during experiment creation:", error);
      openToast("An error occurred during experiment creation");
      setSubmitting(false);
    }
  };

  const handleBatchNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setBatchName(newValue);

    if (newValue) {
      const validationResult = validateModelName(newValue);
      setBatchNameError(validationResult);
    }
  };

  const toggleLoraTraining = () => {
    setTriggerLoraFinetuning(!triggerLoraFinetuning);
    if (!triggerLoraFinetuning) {
      setTriggerBulkUpload(false);
    }
  };

  const handleBulkUploadFileSelect = (file: File) => {
    setBulkUploadFile(file);
  };

  const handleCreateProduct = async () => {
    if (!newProductName.trim()) return;

    setCreatingProduct(true);
    try {
      const product = await createProduct({
        brand_id: brandId,
        name: newProductName.trim()
      });

      if (product) {
        setSelectedProductId(product.id);
        setShowCreateNewProduct(false);
        setNewProductName("");
        openToast("Product created successfully");
      } else {
        openToast("Failed to create product");
      }
    } finally {
      setCreatingProduct(false);
    }
  };

  return (
    <FormDialog
      title="Launch Experiment"
      description={"Kick off an experiment to caption datasets, launch LoRA training, and bulk generate images"}
      open={open}
      onClose={onClose}
      onSubmit={handleCaption}
      primaryButtonText="Submit"
      isPrimaryButtonDisabled={submitting}
      dataDogIdentifier="launch experiment"
    >
      <Flex flexDirection="column">
        <Flex flexDirection="row" py="10px">
          <input id="input=file" name="file" required={true} type="file" webkitdirectory="" />
        </Flex>
        <Box pb="8px">
          <Typography variant="body1">Experiment name</Typography>
        </Box>
        <TextField
          type="text"
          name="batchName"
          value={batchName}
          onChange={handleBatchNameChange}
          placeholder="Experiment Name"
          required
          fullWidth
          error={Boolean(batchNameError)}
          helperText={batchNameError}
          dataDogIdentifier="launch experiment name"
        />

        <Box pb="8px" pt="16px">
          <Typography variant="body1">Select Product</Typography>
        </Box>
        <FormControl fullWidth>
          <Select
            value={selectedProductId || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "create_new") {
                setShowCreateNewProduct(true);
                setSelectedProductId(undefined);
              } else {
                setSelectedProductId(value as number);
                setShowCreateNewProduct(false);
              }
            }}
            displayEmpty
            disabled={productsLoading || creatingProduct}
          >
            <MenuItem value="">
              <em>None (Optional)</em>
            </MenuItem>
            {products.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.name}
              </MenuItem>
            ))}
            <MenuItem value="create_new">
              <em>+ Create New Product</em>
            </MenuItem>
          </Select>
          {productsLoading && <Typography variant="caption">Loading products...</Typography>}
        </FormControl>

        {showCreateNewProduct && (
          <Box mt={2} p={2} border="1px solid" borderColor="divider" borderRadius={1}>
            <Typography variant="subtitle2" gutterBottom>
              Create New Product
            </Typography>
            <TextField
              type="text"
              value={newProductName}
              onChange={(e) => setNewProductName(e.target.value)}
              placeholder="Enter new product name"
              fullWidth
              disabled={creatingProduct}
              sx={{ mb: 2 }}
              dataDogIdentifier="launch experiment new product name"
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleCreateProduct}
              disabled={!newProductName.trim() || creatingProduct}
              dataDogIdentifier="launch experiment create new product"
            >
              {creatingProduct ? "Creating..." : "Create Product"}
            </Button>
          </Box>
        )}

        <Box pb="8px" pt="16px">
          <Typography variant="body1">Description</Typography>
        </Box>
        <TextField
          type="text"
          name="description"
          value={description}
          multiline={true}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Experiment description"
          fullWidth
          dataDogIdentifier="launch experiment description"
        />

        <Box pb="8px">
          <Typography variant="body1">Trigger word</Typography>
        </Box>
        <TextField
          type="text"
          name="triggerWord"
          value={triggerWord}
          size="medium"
          onChange={(e) => setTriggerWord(e.target.value)}
          placeholder="Trigger Word"
          fullWidth
          dataDogIdentifier="launch experiment trigger word"
        />
        <Box pb="8px">
          <Typography variant="body1">Prompt</Typography>
        </Box>
        <TextField
          type="text"
          name="prompt"
          value={prompt}
          multiline={true}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Prompt"
          size="medium"
          fullWidth
          dataDogIdentifier="launch experiment prompt"
        />
      </Flex>
      <Box pb="8px" />

      <Box pb="8px">
        <Button
          variant="contained"
          onClick={toggleLoraTraining}
          dataDogIdentifier="launch experiment toggle lora training"
        >
          {triggerLoraFinetuning ? "Disable LoRA Training" : "Enable LoRA Training"}
        </Button>
      </Box>
      {triggerLoraFinetuning && (
        <div>
          <Box pb="8px" pt="16px">
            <Typography variant="body1">Reference Images</Typography>
          </Box>
          <input
            id="reference-image"
            name="reference-image"
            type="file"
            accept="image/*"
            multiple
            onChange={handleReferenceImageChange}
          />
          {referenceImages && (
            <Box pb="8px">
              <Typography variant="caption" color="text.secondary">
                Selected: {referenceImages.map((image) => image.name).join(", ")}
              </Typography>
            </Box>
          )}

          <Box pb="16px" />
          <Typography variant="h6">LoRA Model Training Parameters</Typography>
          {isLoading ? (
            <Typography variant="caption">Loading experiment info...</Typography>
          ) : (
            <LoraTrainingForm data={loratrainingData} onChange={setLoraTrainingData} brandId={brandId} />
          )}
          <Box pb="8px">
            <Typography variant="body1">AutoEval Datapath in S3 (Optional)</Typography>
          </Box>
          <TextField
            type="text"
            name="prompt"
            value={autoEvalDatapath}
            multiline={false}
            onChange={(e) => setAutoEvalDatapath(String(e.target.value))}
            placeholder="AutoEval Datapath"
            size="medium"
            fullWidth
            dataDogIdentifier="launch experiment auto eval datapath"
          />
          <Box pb="8px" pt="16px">
            <Typography variant="body1">Eval Loss Dataset</Typography>
          </Box>
          <input
            id="eval-loss-dataset"
            name="eval-loss-dataset"
            type="file"
            accept=".zip,application/zip,application/x-zip,application/x-zip-compressed"
            onChange={handleEvalLossDatasetChange}
          />
          {evalLossDataset && (
            <Typography variant="caption" color="text.secondary">
              Selected: {evalLossDataset.name}
            </Typography>
          )}
        </div>
      )}
      <Box pb="8px" />
      <Box pb="8px">
        <Button
          variant="contained"
          onClick={() => setTriggerBulkUpload(!triggerBulkUpload)}
          dataDogIdentifier="launch experiment toggle custom bulk generation"
        >
          {triggerBulkUpload ? "Disable Custom Bulk Generation" : "Enable Custom Bulk Generation"}
        </Button>
      </Box>
      {triggerBulkUpload && (
        <div>
          <Typography variant="h6">Bulk Upload Generation</Typography>
          <BulkUploadForm onFileSelect={handleBulkUploadFileSelect} />
        </div>
      )}
    </FormDialog>
  );
};

export default LaunchExperimentDialog;
